/**
 * SQLite3 WASM loader — shared between legacy and sqlite3mt paths.
 *
 * Extracted from sqlite-worker.ts so that the new sqlite3mt runtime
 * can load the WASM module without pulling in the legacy VFS, SAB
 * manager, or page-change tracker.
 *
 * The function is idempotent — safe to call multiple times.
 */

import {
    emitSecureBlobComponentProgress,
    hasSecureBlobComponentJobRuntimeEnv,
} from './componentRuntimeProgress'

let _sqlite3Promise: Promise<any> | null = null

declare const __SECUREBLOB_COMPONENT_SQLITE3_WASM_BASE64__: string | undefined

type Sqlite3ApiBootstrapLike = {
    initializersAsync?: unknown[]
}

const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

function decodeBase64ToBytes(base64: string): Uint8Array {
    const normalized = base64.replace(/[\r\n\t ]/g, '').replace(/-/g, '+').replace(/_/g, '/')
    const outputLength = Math.floor((normalized.length * 3) / 4)
        - (normalized.endsWith('==') ? 2 : normalized.endsWith('=') ? 1 : 0)
    const output = new Uint8Array(Math.max(0, outputLength))
    let buffer = 0
    let bits = 0
    let offset = 0

    for (const char of normalized) {
        if (char === '=') break
        const value = BASE64_CHARS.indexOf(char)
        if (value < 0) {
            throw new Error('Invalid base64 character in embedded sqlite3.wasm payload.')
        }

        buffer = (buffer << 6) | value
        bits += 6
        if (bits >= 8) {
            bits -= 8
            if (offset < output.length) {
                output[offset++] = (buffer >> bits) & 0xff
            }
        }
    }

    return output
}

function readEmbeddedComponentSqliteWasmBinary(): Uint8Array | null {
    if (typeof __SECUREBLOB_COMPONENT_SQLITE3_WASM_BASE64__ === 'undefined'
        || __SECUREBLOB_COMPONENT_SQLITE3_WASM_BASE64__.length === 0) {
        return null
    }

    return decodeBase64ToBytes(__SECUREBLOB_COMPONENT_SQLITE3_WASM_BASE64__)
}

function stripUnusedOpfsInitializers(): void {
    const bootstrap = (globalThis as typeof globalThis & {
        sqlite3ApiBootstrap?: Sqlite3ApiBootstrapLike
    }).sqlite3ApiBootstrap

    if (!bootstrap || !Array.isArray(bootstrap.initializersAsync)) {
        return
    }

    bootstrap.initializersAsync = bootstrap.initializersAsync.filter(initializer => {
        if (typeof initializer !== 'function') {
            return true
        }

        const source = Function.prototype.toString.call(initializer)
        return !source.includes('installOpfsVfs')
    })
}

/**
 * Initialize the sqlite3 WASM module.
 * Caches the result — safe to call multiple times.
 */
export async function initSqlite3(): Promise<any> {
    if (_sqlite3Promise) return _sqlite3Promise

    _sqlite3Promise = (async () => {
        emitSecureBlobComponentProgress('sqlite-wasm-loader', 'Importing sqlite-wasm module')
        // Dynamic import to support both browser and Node
        const mod = await import('@sqlite.org/sqlite-wasm')
        emitSecureBlobComponentProgress('sqlite-wasm-loader', 'Imported sqlite-wasm module')
        // sqlite3-wasm eagerly registers an async OPFS bootstrap hook. SecureBlob
        // never uses that VFS in the browser worker path, and Safari private mode
        // can make the probe fail noisily for every worker. Strip it before
        // bootstrapping so the custom sqlite3mt VFS remains the only storage path.
        stripUnusedOpfsInitializers()
        const wasmBinary = readEmbeddedComponentSqliteWasmBinary()
        if (hasSecureBlobComponentJobRuntimeEnv() && wasmBinary == null) {
            throw new Error(
                'sqlite3.wasm is not embedded in this component job artifact; rebuild the component with the current SecureBlob component runtime.'
            )
        }
        emitSecureBlobComponentProgress('sqlite-wasm-loader', 'Initializing sqlite-wasm module', {
            embeddedWasmBytes: wasmBinary?.byteLength ?? 0,
        })
        const initModule = (mod.default ?? mod) as (moduleArg?: Record<string, unknown>) => Promise<any>
        const sqlite3 = await initModule(wasmBinary == null ? {} : { wasmBinary })
        emitSecureBlobComponentProgress('sqlite-wasm-loader', 'Initialized sqlite-wasm module')
        return sqlite3
    })()

    return _sqlite3Promise
}

/**
 * Reset the cached sqlite3 module (for testing).
 */
export function resetSqlite3Cache(): void {
    _sqlite3Promise = null
}
